import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { BookOpen, Briefcase, TrendingUp, CircleCheckBig } from 'lucide-react';
import Navbar from '../../components/navigation/Navbar';
import background from '../../images/Groupimage1.jpeg'
import Footer from '../../components/Footer';

const LandingPage = () => {
  const fadeInUp = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  const staggerContainer = {
    animate: {
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const partnerLogos = [
    { id: 1, src: "Agra.svg", alt: "AGRA" },
    { id: 2, src: "lfgg.svg", alt: "LFG" },
    { id: 3, src: "legon.svg", alt: "University of Ghana" },
  ];

  return (
    <div className="min-h-screen bg-white">
      <header className="bg-white text-green-500 relative">
        <Navbar />
      </header>

      <section className="relative min-h-[600px] flex items-center justify-center bg-gradient-to-br from-green-900 via-green-800 to-green-900">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-80"
          style={{ backgroundImage: `url(${background})` }}
        ></div>
        <div className="absolute inset-0 bg-green-900/60"></div>

        <div className="relative z-10 container mx-auto px-4 sm:px-6 py-12 sm:py-20 text-center">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="inline-flex items-center gap-2 bg-gradient-to-r from-[#EBE8E87D] to-[#CDCACA66] px-3 sm:px-4 py-2 rounded-full mb-4 sm:mb-6"
          >
            <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></span>
            <span className="text-xs sm:text-sm text-white">Now accepting applications</span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4 sm:mb-6 px-2"
          >
            Join the Network of Future Food Leaders
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="text-base sm:text-lg md:text-xl text-white mb-6 sm:mb-8 max-w-2xl mx-auto px-2"
          >
            Get access to skills training, employment pathways and entrepreneurship support.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.5 }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <Link
              to="/consent"
              className="text-white px-6 sm:px-8 py-3 sm:py-4 rounded-lg text-base sm:text-lg font-semibold transition-all mb-8 sm:mb-12 inline-block hover:opacity-90"
              style={{ background: 'linear-gradient(135deg, #F89D08 0%, #925C05 100%)' }}
            >
              Start Your Registration Now
            </Link>
          </motion.div>

          <motion.div
            variants={staggerContainer}
            initial="initial"
            animate="animate"
            className="grid grid-cols-1 sm:grid-cols-3 gap-6 sm:gap-8 max-w-4xl mx-auto"
          >
            {[
              { number: "10,000+", label: "Youth Trained" },
              { number: "85%", label: "Employment Rate" },
              { number: "5", label: "Regions Covered" }
            ].map((stat, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                className="text-white"
              >
                <div className="text-3xl sm:text-4xl font-bold mb-2">{stat.number}</div>
                <div className="text-base sm:text-lg opacity-90">{stat.label}</div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      <section className="bg-gray-100 py-16">
  <div className="container mx-auto px-4 sm:px-6">
    <motion.h2
      initial={{ opacity: 0 }}
      whileInView={{ opacity: 1 }}
      viewport={{ once: true }}
      className="text-green-900 text-center text-xl sm:text-2xl font-bold uppercase mb-12"
    >
      Powered by Trusted Agro-Development Partners
    </motion.h2>
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-8 items-center justify-items-center"
    >
      {partnerLogos.map((logo) => (
        <div
          key={logo.id}
          className=" p-4 sm:p-6 flex items-center justify-center w-full h-32 sm:h-40"
        >
          <img
            src={logo.src}
            alt={logo.alt}
            className={`object-contain ${logo.id === 3 ? 'h-40 w-40 sm:h-40 sm:w-40' : 'h-15 w-auto'}`}
          />
        </div>
      ))}
    </motion.div>
  </div>
</section>

      {/* What You'll Gain Section */}
      <section id="benefits" className="bg-white py-20">
        <div className="container mx-auto px-4 sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-green-900 mb-4">What You'll Gain</h2>
            <p className="text-green-900 text-base sm:text-lg max-w-2xl mx-auto">
              The EYES Ghana Program provides comprehensive support to help you succeed in the agricultural sector.
            </p>
          </motion.div>

          <motion.div
            variants={staggerContainer}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8"
          >
            {[
              {
                icon: BookOpen,
                title: "Skills Training",
                description: "Gain practical skills in modern agricultural techniques, technology and business management."
              },
              {
                icon: Briefcase,
                title: "Employment",
                description: "Connect with leading agribusinesses and access job placement opportunities across Ghana."
              },
              {
                icon: TrendingUp,
                title: "Entrepreneurship",
                description: "Receive mentorship, startup support and access to funding for your own agribusiness venture."
              }
            ].map((benefit, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                whileHover={{ y: -5 }}
                className="bg-white rounded-lg shadow-lg p-6 sm:p-8 border border-gray-200"
              >
                <div className="w-14 h-14 sm:w-16 sm:h-16 bg-green-100 rounded-full flex items-center justify-center mb-6">
                  <benefit.icon className="w-7 h-7 sm:w-8 sm:h-8 text-green-700" />
                </div>
                <h3 className="text-xl sm:text-2xl font-bold text-gray-800 mb-4">{benefit.title}</h3>
                <p className="text-gray-600 text-sm sm:text-base">{benefit.description}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      <section className="bg-green-900 py-20">
        <div className="container mx-auto px-4 sm:px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="inline-flex items-center gap-3 bg-gradient-to-r from-[#EBE8E87D] to-[#CDCACA66] px-8 py-2 rounded-full mb-6">
              <CircleCheckBig className="w-5 h-5 text-[#F79C07]" />
              <span className="text-white font-semibold">Eligibility</span>
            </div>

            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">Who Can Register?</h2>

            <p className="text-white text-base sm:text-lg mb-8 max-w-2xl mx-auto">
              Youth aged <span className="font-bold">18 to 35 years</span> residing in Ghana are eligible to apply for the EYES Ghana Program.
            </p>

            <motion.div
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <Link
                to="/consent"
                className="text-white px-6 sm:px-8 py-3 sm:py-4 rounded-lg text-base sm:text-lg font-semibold transition-all mb-6 inline-block hover:opacity-90"
                style={{ background: 'linear-gradient(135deg, #F89D08 0%, #925C05 100%)' }}
              >
                Start Your Registration Now
              </Link>
            </motion.div>

            <div className="text-green-200 text-sm space-y-1">
              <p>Registration is free and takes less than 5 minutes</p>
              <p>All data is protected & confidential</p>
            </div>
          </motion.div>
        </div>
      </section>

        <Footer />

    </div>
  );
};

export default LandingPage;